import { useMemo } from 'react';
import React from 'react';
import {
  MaterialReactTable,
  useMaterialReactTable,
  type MRT_ColumnDef,
} from 'material-react-table';

interface AdvanceEntry {
  id: string, 
  business_id: string, 
  offer_id: string, 
  payout_bank_account_id: string,
  terms: { [key: string]: any },
  payments_start_at: string,
  outstanding_balance: { [key: string]: any },
  execution_status: string,
  created_at: string,
  executed_at: string,
  missing_agreements: { [key: string]: any }
}

interface AdvanceTableProps {
  advanceData: { data: AdvanceEntry[] }; // Ensure this matches the type you're passing
  setAdvanceId: (advanceId: string) => void;
}



const AdvanceTable: React.FC<AdvanceTableProps> = ({ advanceData, setAdvanceId }) => {
  // Memoized columns to prevent re-creation on re-renders
  const columns = useMemo<MRT_ColumnDef<AdvanceEntry>[]>(
    () => [
      {
        accessorKey: 'id',
        header: 'ID',
        size: 100,
        Cell: ({ row }) => (
          <a
              href={`#`}
              onClick={(e) => {
                e.preventDefault(); // Prevents navigation to "#"
                setAdvanceId(row.original.id); // Calls the function with the row's id
              }}
              rel="noopener noreferrer"
          >
              {row.original.id}
          </a>
      ),
    },
    {
      accessorKey: 'created_at',
      header: 'Created At',
      size: 100,
      Cell: ({ row }) => new Date(row.original.created_at).toLocaleString(),  // Format date
  },
    {
        accessorKey: 'business_id',
        header: 'Business ID',
        size: 100,
        Cell: ({ row }) => (
            <a
                href={`/business?business_id=${row.original.business_id}`}
                rel="noopener noreferrer"
            >
                {row.original.business_id}
            </a>
        ),
    },
    {
        accessorKey: 'offer_id',
        header: 'Offer ID',
        size: 100,
    },
    {
        accessorKey: 'payout_bank_account_id',
        header: 'Payout Bank Account ID',
        size: 100,
    },
    {
      accessorKey: 'terms.amount',
      header: 'Principal',
      size: 100,
      Cell: ({ row }) => `${row.original.terms.currency.toUpperCase()} ${(row.original.terms.amount / 100).toFixed(2)}`,

  },
  {
    accessorKey: 'terms.fee',
    header: 'Fee',
    size: 100,
    Cell: ({ row }) => `${row.original.terms.currency.toUpperCase()} ${(row.original.terms.fee / 100).toFixed(2)}`,

},
{
  accessorKey: 'terms.target_payback_period_days',
  header: 'Target Payback Period (Days)',
  size: 100,
},
{
  accessorKey: 'terms.hold_rate',
  header: 'Hold Rate',
  size: 100,
  Cell: ({ row }) => {
    const holdRate = parseFloat(row.original.terms.hold_rate); // Convert to a number
    
    // Check if holdRate is a valid number
    if (isNaN(holdRate)) {
      return "Invalid Hold Rate"; // Return fallback if not a valid number
    }
    
    return `${holdRate.toFixed(2)}`; // Format to 2 decimal places
  },
  
},
    {
        accessorKey: 'payments_start_at',
        header: 'Payments Start At',
        size: 100,
        Cell: ({ row }) => new Date(row.original.payments_start_at).toLocaleDateString(),  // Format date
    },
    {
        accessorKey: 'outstanding_balance',
        header: 'Outstanding Balance',
        size: 100,
        Cell: ({ row }) => {
          const { principal, fee, total } = row.original.outstanding_balance;
          return `
                  Principal: ${row.original.terms.currency.toUpperCase()}  ${(principal / 100).toFixed(2)},<br /> 
                  Fee: ${row.original.terms.currency.toUpperCase()}  ${(fee / 100).toFixed(2)},<br /> 
                  Total: ${row.original.terms.currency.toUpperCase()}  ${(total / 100).toFixed(2)}`;
      },
    },
    {
        accessorKey: 'execution_status',
        header: 'Execution Status',
        size: 100,
    },
    {
        accessorKey: 'executed_at',
        header: 'Executed At',
        size: 100,
        Cell: ({ row }) => row.original.executed_at ? new Date(row.original.executed_at).toLocaleString() : '-',
    },
    {
        accessorKey: 'missing_agreements',
        header: 'Missing Agreements',
        size: 100,
        Cell: ({ row }) => (
          <pre>{JSON.stringify(row.original.missing_agreements, null, 2)}</pre>  // Pretty print the terms object
      ),    }
    ],
    [],
  );

  const table = useMaterialReactTable({
    columns,
    data: advanceData.data, // Matches the shape of your updated webhookData
    initialState: { columnVisibility: { terms:false,payout_bank_account_id:false, missing_agreements: false, business_id: false, offer_id:false, paymenets_start_at:false} }, 
  });

  return <MaterialReactTable table={table} />;
};

export default AdvanceTable;
